import { useAppStore } from '../../store/useAppStore';
import { Project } from '../../types';
import { useState, useEffect, useRef } from 'react';
import { useSound } from '../../hooks/audio/useSound';
import { motion } from 'framer-motion';

interface ProjectIndicatorProps {
  projects: Project[];
}

export const ProjectIndicator: React.FC<ProjectIndicatorProps> = ({
  projects,
}) => {
  const nearestProjectIndex = useAppStore(state => state.nearestProjectIndex);
  const isInitialAnimationComplete = useAppStore(
    state => state.isInitialAnimationComplete
  );
  const selectedIndex = useAppStore(state => state.selectedIndex);
  const hasVisitedAbout = useAppStore(state => state.hasVisitedAbout);
  const setScrollOffset = useAppStore(state => state.setScrollOffset);
  const setSelected = useAppStore(state => state.setSelected);
  const { playUI, playProjectOpenWind } = useSound();
  const [showNearest, setShowNearest] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const hasEverSelectedProjectRef = useRef(false);
  const indicatorButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [hasCompletedInitialAnimation, setHasCompletedInitialAnimation] =
    useState(false);

  if (selectedIndex !== null) {
    hasEverSelectedProjectRef.current = true;
  }

  const shouldAnimate =
    !hasEverSelectedProjectRef.current &&
    !hasCompletedInitialAnimation &&
    !hasVisitedAbout;

  useEffect(() => {
    if (isInitialAnimationComplete) {
      setShowNearest(true);
    }
  }, [isInitialAnimationComplete]);

  useEffect(() => {
    if (!shouldAnimate) return;
    const maxDelay = 1000 + (projects.length - 1) * 120 + 500;
    const id = setTimeout(() => setHasCompletedInitialAnimation(true), maxDelay);
    return () => clearTimeout(id);
  }, [shouldAnimate, projects.length]);

  if (selectedIndex !== null) return null;

  // Ordre des points : 1/9 en haut, 9/9 en bas (aligné avec getDisplayIndex = totalProjects - originalIndex)
  const reorderedIndexes = projects.map((_, i) => i);

  const handleClick = (targetIndex: number) => {
    if (!isInitialAnimationComplete) return;

    playUI('nav', 'click');
    useAppStore.getState().setHasScrolled(true);
    useAppStore.getState().setIsIndicatorScrolling(true);

    const n = projects.length;
    const anglePerProject = (Math.PI * 2) / n;
    const middleOffset = Math.floor(n / 2);
    const currentOffset = useAppStore.getState().scrollOffset;

    // Position exacte pour que targetIndex soit au centre (sans arrondi)
    const targetNormalized =
      ((middleOffset - targetIndex) % n + n) % n * anglePerProject;
    let targetOffset = targetNormalized / 2;

    // Chemin le plus court : ajouter ±π pour trouver l'équivalent le plus proche
    const period = Math.PI;
    const diff = targetOffset - currentOffset;
    if (diff > period / 2) targetOffset -= period;
    else if (diff < -period / 2) targetOffset += period;

    const startOffset = currentOffset;
    const distance = targetOffset - startOffset;
    const duration = 1000;
    const startTime = performance.now();

    const WIND_PEAK_VELOCITY = 0.009;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentPosition = startOffset + distance * eased;
      setScrollOffset(currentPosition);

      // Courbe de vent fluide (cloche) au lieu d'un pic brutal
      const windVelocity =
        WIND_PEAK_VELOCITY * Math.sin(progress * Math.PI);
      useAppStore.getState().setVelocity(windVelocity);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        useAppStore.getState().setIsIndicatorScrolling(false);
        useAppStore.getState().setVelocity(0);
      }
    };

    requestAnimationFrame(animate);
  };

  return (
    <div className="fixed left-6 top-1/2 z-20 -translate-y-1/2 md:left-8">
      <div className="mb-3 pl-[14px]">
        <p className="label text-[10px] tracking-[0.18em] text-black/55">
          Navigate projects
        </p>
        <p className="body text-[12px] leading-relaxed text-black/50">
          Scroll, use arrows, or click dots
        </p>
      </div>
      <div className="relative flex flex-col-reverse gap-1.5">
      <motion.div
        className="pointer-events-none absolute left-0 top-1 bottom-1 w-5"
        animate={{ opacity: [0.55, 0.8, 0.55] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      >
        <svg
          viewBox="0 0 20 100"
          preserveAspectRatio="none"
          className="h-full w-full"
          aria-hidden="true"
        >
          <path
            d="M10 0 C1 7,19 15,10 23 C1 31,19 39,10 47 C1 55,19 63,10 71 C1 79,19 87,10 95 C4 98,16 99,10 100"
            fill="none"
            stroke="rgba(0,0,0,0.16)"
            strokeWidth="1.15"
            strokeLinecap="round"
          />
          <motion.path
            d="M10 0 C1 7,19 15,10 23 C1 31,19 39,10 47 C1 55,19 63,10 71 C1 79,19 87,10 95 C4 98,16 99,10 100"
            fill="none"
            stroke="rgba(0,0,0,0.26)"
            strokeWidth="1.35"
            strokeLinecap="round"
            strokeDasharray="12 16"
            animate={{
              strokeDashoffset: [0, -112],
              strokeOpacity: [0.42, 0.72, 0.42],
            }}
            transition={{ duration: 3.8, repeat: Infinity, ease: 'linear' }}
          />
        </svg>
      </motion.div>
      {reorderedIndexes.map((originalIndex, i) => {
        const isCurrent = originalIndex === nearestProjectIndex;
        const isHovered = hoveredIndex === originalIndex;
        const showTitle =
          isInitialAnimationComplete && (isCurrent || isHovered);

        return (
          <button
            key={originalIndex}
            type="button"
            ref={el => {
              indicatorButtonRefs.current[originalIndex] = el;
            }}
            onClick={() => handleClick(originalIndex)}
            onKeyDown={event => {
              if (!isInitialAnimationComplete) return;

              if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                event.preventDefault();
                event.stopPropagation();

                const direction = event.key === 'ArrowRight' ? 1 : -1;
                const nextIndex =
                  (originalIndex + direction + projects.length) % projects.length;
                handleClick(nextIndex);
                indicatorButtonRefs.current[nextIndex]?.focus();
                return;
              }

              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              if (!isInitialAnimationComplete) return;

              if (originalIndex === nearestProjectIndex) {
                playProjectOpenWind();
                setSelected(projects[originalIndex], originalIndex);
              } else {
                handleClick(originalIndex);
              }
            }}
            onMouseEnter={() => setHoveredIndex(originalIndex)}
            onMouseLeave={() => setHoveredIndex(null)}
            className="group relative flex h-5 w-5 items-center justify-center cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-black/35 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f7f7f7]"
            aria-label={`Go to project ${projects[originalIndex]?.title ?? originalIndex + 1}`}
            title={`Go to ${projects[originalIndex]?.title ?? `project ${originalIndex + 1}`}`}
            aria-current={isCurrent ? 'true' : undefined}
          >
            <div
              className={`
                shrink-0 rounded-full transition-all duration-300 ease-out
                ${shouldAnimate ? 'opacity-0' : 'opacity-100'}
                ${
                  isInitialAnimationComplete
                    ? showNearest && isCurrent
                      ? 'h-3 w-3 bg-black scale-[1.2] shadow-[0_0_0_3px_rgba(0,0,0,0.08)]'
                      : isHovered
                        ? 'h-3 w-3 bg-black/40'
                        : 'h-2.5 w-2.5 bg-black/18 group-hover:h-3 group-hover:w-3 group-hover:bg-black/40'
                    : 'h-2.5 w-2.5 bg-black/18'
                }
              `}
              style={
                shouldAnimate
                  ? {
                      animation: 'fade-in 0.5s ease-out forwards',
                      animationDelay: `${1000 + (reorderedIndexes.length - 1 - i) * 120}ms`,
                    }
                  : undefined
              }
            />
            {showTitle && (
              <span
                className={`indicator-title body pointer-events-none absolute left-full ml-3 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-xl border border-black/[0.16] bg-white/[0.78] px-4 py-2 text-[0.95rem] backdrop-blur-sm ${
                  isHovered && !isCurrent ? 'text-black/62' : 'text-black/85'
                }`}
              >
                {projects[originalIndex]?.title} – {projects.length - originalIndex}/{projects.length}
              </span>
            )}
          </button>
        );
      })}
      </div>
    </div>
  );
};
